import Search from '@mui/icons-material/Search';
import {
  Box,
  InputAdornment,
  Paper,
  styled,
  type SxProps,
  type Theme,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { proAccountThemeCoreUI } from '@orcha/micro-library-core-ui';
import type { SyntheticEvent } from 'react';

const GroupHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  color: theme.palette.grey[500],
  fontWeight: theme.typography.fontWeightMedium,
}));

const ListHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(2, 2, 1),
  color: theme.palette.primary.main,
  fontWeight: theme.typography.fontWeightMedium,
}));

const GroupItems = styled('ul')({
  padding: 0,
});

const Option = styled('li')(({ theme }) => ({
  paddingLeft: `${theme.spacing(3.5)} !important`,
  fontWeight: theme.typography.fontWeightMedium,
}));

export interface ISearchSuggestionOption {
  suggestionType?: string;
  groupName?: string;
  optionName: string;
}

export type SearchAutocompleteProps = {
  optionsHeader?: string;
  searchSuggestionOptions?: ISearchSuggestionOption[];
  onInputChange: (event: SyntheticEvent<Element, Event>, value: string) => void;
  inputValue: string;
  onOptionSelect: (option: ISearchSuggestionOption) => void;
  onFreeTextSubmit: () => void;
  inputLabel: string;
  placeholderText?: string;
  sx?: SxProps<Theme>;
  size?: 'small' | 'medium';
};

const CustomPaper = (props: any, listHeader?: string) => {
  return (
    <Paper
      {...props}
      elevation={8}
      sx={{ marginTop: proAccountThemeCoreUI.spacing(1) }}
    >
      {listHeader && <ListHeader>{listHeader}</ListHeader>}
      {props.children}
    </Paper>
  );
};

/**
 * SearchAutocomplete component from [MicroLibrarySearchUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibrarySearchUI)
 * @param props - SearchAutocompleteProps
 * @returns SearchAutocomplete component
 */

export const SearchAutocomplete = ({
  searchSuggestionOptions,
  optionsHeader,
  onInputChange,
  inputValue,
  onOptionSelect,
  onFreeTextSubmit,
  inputLabel,
  placeholderText,
  sx,
  size,
}: SearchAutocompleteProps) => {
  const shouldShowHeader =
    searchSuggestionOptions && searchSuggestionOptions.length > 0;

  return (
    <Autocomplete
      sx={sx}
      size={size}
      selectOnFocus
      handleHomeEndKeys
      options={searchSuggestionOptions ?? []}
      groupBy={(option: ISearchSuggestionOption) => option?.groupName ?? ''}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.optionName
      }
      renderOption={(props, option) => {
        return (
          <Option {...props} onClick={() => onOptionSelect(option)}>
            {option.optionName}
          </Option>
        );
      }}
      freeSolo
      onInputChange={(event, value, reason) => {
        if (reason === 'input' || reason === 'clear') {
          onInputChange(event, value);
        }
      }}
      inputValue={inputValue ?? ''}
      renderInput={(params) => (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <TextField
            {...params}
            placeholder={placeholderText}
            inputProps={{
              ...params.inputProps,
              maxLength: 100,
              'data-testid': 'search-input',
              'aria-label': inputLabel,
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              sx: {
                fontSize: proAccountThemeCoreUI.typography.fontSize,
              },
            }}
          />
        </Box>
      )}
      renderGroup={(params) => (
        <li key={params.key}>
          {params.group.length > 0 && <GroupHeader>{params.group}</GroupHeader>}
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
      onChange={(
        _event: any,
        newValue: string | ISearchSuggestionOption | null
      ) => {
        if (newValue === null) {
          return;
        }

        if (typeof newValue === 'string') {
          onFreeTextSubmit();
        } else {
          onOptionSelect(newValue);
        }
      }}
      PaperComponent={
        shouldShowHeader
          ? (props) => CustomPaper(props, optionsHeader)
          : undefined
      }
      ListboxProps={{ sx: { padding: proAccountThemeCoreUI.spacing(0, 0, 1) } }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterOptions={(x: any) => x}
    />
  );
};
